/**
 * Map a text selection made in the pdf.js text layer back to offsets in the page text the
 * backend serves.
 *
 * The two strings describe the same page but are not identical: the backend's `PageModel.text` is
 * the exact PDFium character sequence with `\r\n` line ends, while a browser selection carries
 * whatever whitespace the text layer's spans happened to contain. Matching therefore runs over a
 * whitespace-normalised copy of both, with an index map that translates a hit back to the original
 * offsets the API expects (ADR 0007).
 */

/** One place in the page text where the selected string occurs. */
export interface SelectionCandidate {
  /** Offsets into the original page text, ready for `POST /documents/{id}/findings`. */
  start: number;
  end: number;
  /** The line the occurrence sits on, for the popover to show when several occurrences match. */
  line: string;
  /** 1-based line number, matching `FindingModel.line`. */
  lineNumber: number;
}

export type SelectionResolution =
  | { kind: 'none' }
  | { kind: 'unique'; candidate: SelectionCandidate }
  | { kind: 'ambiguous'; candidates: SelectionCandidate[] };

interface NormalisedText {
  /** Whitespace collapsed to single spaces, trimmed at both ends. */
  text: string;
  /** `map[i]` is the offset in the original string that produced `text[i]`. */
  map: number[];
}

/**
 * Collapse every run of whitespace into one space and remember where each kept character came from.
 *
 * Leading and trailing whitespace is dropped entirely, so a selection that swept up a newline at
 * either end still matches the words the user meant.
 */
export const normaliseWithMap = (input: string): NormalisedText => {
  const chars: string[] = [];
  const map: number[] = [];
  let pendingSpace = false;

  for (let index = 0; index < input.length; index += 1) {
    const char = input[index];
    if (/\s/.test(char)) {
      // A space is only emitted once something follows it, which drops trailing whitespace.
      if (chars.length > 0) pendingSpace = true;
      continue;
    }
    if (pendingSpace) {
      chars.push(' ');
      map.push(index);
      pendingSpace = false;
    }
    chars.push(char);
    map.push(index);
  }

  return { text: chars.join(''), map };
};

/** Every index in `haystack` where `needle` starts, including overlapping occurrences. */
const allOccurrences = (haystack: string, needle: string): number[] => {
  const found: number[] = [];
  if (needle === '') return found;
  let from = 0;
  let at = haystack.indexOf(needle, from);
  while (at !== -1) {
    found.push(at);
    from = at + 1;
    at = haystack.indexOf(needle, from);
  }
  return found;
};

/** The line of `pageText` containing `offset`, and its 1-based number. */
const lineAt = (pageText: string, offset: number): { line: string; lineNumber: number } => {
  const before = pageText.slice(0, offset);
  const lineNumber = before.split('\n').length;
  const start = before.lastIndexOf('\n') + 1;
  const end = pageText.indexOf('\n', offset);
  const line = pageText.slice(start, end === -1 ? pageText.length : end).replace(/\r$/, '');
  return { line, lineNumber };
};

/**
 * Resolve a selected string to character offsets in the page text.
 *
 * `context` is the text of the text-layer element the selection started in — pdf.js emits one
 * element per text item, which is usually a line or a run inside it. When the selected string
 * occurs several times on the page, the occurrence whose own line contains that context wins;
 * that resolves the common case of the same word appearing in a header and in the body. If the
 * context does not separate them either, every candidate is returned so the UI can ask.
 */
export const resolveSelection = (pageText: string, selected: string, context = ''): SelectionResolution => {
  const normalisedPage = normaliseWithMap(pageText);
  const normalisedSelection = normaliseWithMap(selected);
  if (normalisedSelection.text === '') return { kind: 'none' };

  const hits = allOccurrences(normalisedPage.text, normalisedSelection.text);
  if (hits.length === 0) return { kind: 'none' };

  const candidates: SelectionCandidate[] = hits.map((hit) => {
    const start = normalisedPage.map[hit];
    // The last matched character maps to its own original offset; the finding ends after it.
    const lastIndex = hit + normalisedSelection.text.length - 1;
    const end = normalisedPage.map[lastIndex] + 1;
    const { line, lineNumber } = lineAt(pageText, start);
    return { start, end, line, lineNumber };
  });

  if (candidates.length === 1) return { kind: 'unique', candidate: candidates[0] };

  const normalisedContext = normaliseWithMap(context).text;
  if (normalisedContext !== '') {
    const byContext = candidates.filter((candidate) =>
      normaliseWithMap(candidate.line).text.includes(normalisedContext)
    );
    if (byContext.length === 1) return { kind: 'unique', candidate: byContext[0] };
    if (byContext.length > 1) return { kind: 'ambiguous', candidates: byContext };
  }

  return { kind: 'ambiguous', candidates };
};

/** True when the node is inside the given container element (a selection that left the page). */
export const isNodeInside = (node: Node | null | undefined, container: Element | null): boolean => {
  if (!node || !container) return false;
  return container.contains(node.nodeType === Node.TEXT_NODE ? node.parentNode : node);
};
